import { SearchModes, type Language } from '@/domainLogic/index.ts';
import type { TranslationFunction } from '@/app/components/translations/Translation.tsx';
import type { SearchParams } from '@/server/searchParams.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import { replaceVar } from '../../../utils/misc';
import { formatDateShortDayMonth } from '../../../utils/dates/utilDates.ts';

interface DecorateShortDateWithWeekParams {
  d: string;
  language: string;
  locale: string;
}

export const decorateAccomodationTypeSingular = (
  accomondationTypeId: number,
  t: TranslationFunction
): string | null => {
  switch (accomondationTypeId) {
    case 1:
      return t('vacation.oss.accommodation.type.hotel');
    case 2:
      return t('vacation.oss.accommodation.type.resort');
    case 3:
      return t('vacation.oss.accommodation.type.apartment');
    case 4:
      return t('vacation.oss.accommodation.type.hostel');
    case 5:
      return t('vacation.oss.accommodation.type.motel');
    case 6:
      return t('vacation.oss.accommodation.type.countryhouse');
    case 7:
      return t('vacation.oss.accommodation.type.guesthouse');
    case 8:
      return t('vacation.oss.accommodation.type.bedandbreakfast');
    case 9:
      return t('vacation.oss.accommodation.type.villa');
    case 10:
      return t('vacation.oss.accommodation.type.boatsandcruise');
    case 11:
      return t('vacation.oss.accommodation.type.campsite');
    case 12:
      return t('vacation.oss.accommodation.type.riad');
    case 13:
      return t('vacation.oss.accommodation.type.ryokan');
    case 14:
      return t('vacation.oss.accommodation.type.aparthotel');
    default:
      return '';
  }
};

export const decorateHotelRating = ({
  n,
  excellent,
  verygood,
  good,
  poor,
  pleasant,
}: {
  n: number;
  excellent: string;
  verygood: string;
  good: string;
  poor: string;
  pleasant: string;
}): string => {
  if (Number.isNaN(n) || n < 60) return poor;
  if (n < 75) return pleasant;
  if (n < 81) return good;
  if (n < 87) return verygood;
  return excellent;
};

export const decorateDateFormat = (date: string, language: string): string => {
  if (language.toLowerCase() === 'de') return date.replace('Sept.', 'Sep.');
  return date;
};

export const isSameMonth = (date1: string, date2: string, language: Language, locale: string) => {
  const date1Month = new Date(date1).toLocaleString(locale, {
    month: 'numeric',
  });

  const date2Month = new Date(date2).toLocaleString(locale, {
    month: 'numeric',
  });

  return date1Month === date2Month;
};

export const decorateShortDateDay = ({ d, language: lang, locale }: DecorateShortDateWithWeekParams): string => {
  const date = new Date(d).toLocaleString(locale, {
    day: 'numeric',
  });

  return decorateDateFormat(date, lang);
};

export const decorateShortDate = ({ d, language: lang, locale }: DecorateShortDateWithWeekParams): string => {
  const date = new Date(d).toLocaleString(locale, {
    day: 'numeric',
    month: 'short',
  });

  return decorateDateFormat(date, lang);
};

export const decorateDates = ({
  locale,
  isWaiting,
  searchParams,
  checkin,
  checkout,
}: {
  locale: string;
  isWaiting: boolean;
  searchParams: SearchParams;
  checkin: string;
  checkout: string;
}) =>
  `${formatDateShortDayMonth(locale, isWaiting ? searchParams[URLParameters.DATE_FROM] : checkin)} - ${formatDateShortDayMonth(
    locale,
    isWaiting ? searchParams[URLParameters.DATE_TO] : checkout
  )}`;

export const decorateNights = (nights: number | null, t: TranslationFunction) => {
  if (!nights || nights === 0) return null;
  if (nights === 1) return `${nights} ${t('vacation.oss.header.widget.calendar.night')}`;
  return `${nights} ${t('vacation.oss.header.widget.calendar.nights')}`;
};

export const decorateNightsStay = (nights: number | null, t: TranslationFunction) => {
  if (!nights || nights === 0) return undefined;
  if (nights === 1) return t('vacation.oss.cards.stay_night', { amount: `${nights}` });
  return t('vacation.oss.cards.stay_nights', { amount: `${nights}` });
};

export const calculateNights = (from: string, to: string): number => {
  const date1 = new Date(from);
  const date2 = new Date(to);
  const DifferenceInTime = date2.getTime() - date1.getTime();
  const DifferenceInDays = DifferenceInTime / (1000 * 3600 * 24);

  return DifferenceInDays;
};

enum MEAL_PLANS {
  ROOM_ONLY = 1,
  BREAKFAST = 2,
  HALF_BOARD = 3,
  FULL_BOARD = 4,
  ALL_INCLUSIVE = 5,
}

export const decorateMealPlan = (mealPlan: number | undefined, t: TranslationFunction): string => {
  switch (mealPlan) {
    case MEAL_PLANS.ROOM_ONLY:
      // return t('vacation.oss.components.searchresults.filterTags.meal.1');
      return '';
    case MEAL_PLANS.BREAKFAST:
      return t('vacation.oss.components.searchresults.filterTags.meal.2');
    case MEAL_PLANS.HALF_BOARD:
      return t('vacation.oss.components.searchresults.filterTags.meal.3');
    case MEAL_PLANS.FULL_BOARD:
      return t('vacation.oss.components.searchresults.filterTags.meal.4');
    case MEAL_PLANS.ALL_INCLUSIVE:
      return t('vacation.oss.components.searchresults.filterTags.meal.5');
    default:
      return '';
  }
};

export const decorateMealPlanMoreDescriptive = (mealPlan: number | undefined, t: TranslationFunction): string => {
  switch (mealPlan) {
    case MEAL_PLANS.BREAKFAST:
      return t('vacation.oss.components.searchfilters.filters.meal.breakfast_incl');
    default:
      return decorateMealPlan(mealPlan, t);
  }
};

export const decorateWaitingNightsStay = (
  dateFrom: string | undefined,
  dateTo: string | undefined,
  t: TranslationFunction
) => {
  if (!dateFrom || !dateTo) return undefined;

  const dt1 = new Date(dateFrom);
  const dt2 = new Date(dateTo);
  const nights = Math.floor(
    (Date.UTC(dt2.getFullYear(), dt2.getMonth(), dt2.getDate()) -
      Date.UTC(dt1.getFullYear(), dt1.getMonth(), dt1.getDate())) /
      (1000 * 60 * 60 * 24)
  );
  if (!nights || nights === 0) return undefined;
  if (nights === 1) return t('vacation.oss.cards.stay_night', { amount: `${nights}` });
  return t('vacation.oss.cards.stay_nights', { amount: `${nights}` });
};

const roundNumber = (val: number) => Math.round(val * 10) / 10;

export const decorateDistance = (value: number, label: string) => {
  const distance = value < 1000 ? `${value} m` : `${roundNumber(value / 1000)} km`;
  return replaceVar(label.replace(/__(.*?)__/g, '{$1}'), { distance });
};

export const decorateProduct = ({ searchMode, t }: { searchMode: SearchModes; t: TranslationFunction }) =>
  searchMode === SearchModes.HO
    ? t('vacation.oss.components.searchmode.ho').toUpperCase()
    : t('vacation.oss.components.searchmode.dp').toUpperCase();
